import React, {FormEvent} from 'react';
import Toggle from "components/Form/Toggle";
import Field from "components/Form/Field";
import {setFilterDates} from './Filter';
import {FieldArray, FieldArrayRenderProps, FormikHelpers} from "formik";
import {SiteVisitFilter, VisitFilter as VisitFilterSpec} from "types/AudienceCriteria";
import Button from "../../../Button";
import {PlusIcon, TrashIcon} from "@heroicons/react/solid";
import Selector from "../../Selector";
import {useQuery} from "react-query";
import ngage from "../../../../services/ngage";
import IconName from "../../../../types/IconName";
import {Item} from "../../Selector/Selector";

// Types ----

type VisitFilterProps = {
  formik: FormikHelpers<any>
  groupIndex: number
  filter?: VisitFilterSpec | null
}

// Helpers ----

const siteVisitFilterTemplate: SiteVisitFilter = {
  operator: '>=',
  count: 1,
  mode: 'ever',
  dates: [],
  site_id: 0
}

const anySiteOption = {
  type: 'any',
  id: 0,
  name: 'Any Site',
  icon: IconName.X,
};

// Component ----

function VisitFilter({formik, groupIndex, filter}: VisitFilterProps) {

  const {data: sites, isLoading: isLoadingSites} = useQuery(
    ['sites.all', {page: 1, perPage: 100}],
    () => ngage.sites.filtered({page: 1, per_page: 1000})
  )

  const siteOptions = [anySiteOption, ...(sites?.data.map(site => ({
    type: 'site',
    id: site.id,
    name: site.name,
    description: site.address?.city,
    icon: IconName.OFFICE_BUILDING
  })) ?? [])];

  function toggleFilter(checked: boolean): void {
    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.visit`,
      checked ? {sites: [{...siteVisitFilterTemplate}]} : undefined
    )
  }

  function selectSite(item: Item, siteVisitIndex: number): void {
    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.visit.sites.${siteVisitIndex}.site_id`,
      item.id
    )
  }

  function selectMode(mode: string, filter: SiteVisitFilter, siteVisitIndex: number): void {
    setFilterDates(
      formik,
      `criteria.groups.${groupIndex}.filters.visit.sites.${siteVisitIndex}.dates`,
      mode,
      filter?.dates || []
    )

    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.visit.sites.${siteVisitIndex}.mode`,
      mode
    )
  }

  return (
    <div className="mt-8">
      <div className="sm:flex items-center">
        <Toggle
          label={`Visited${!filter ? '...' : ''}`}
          checked={!!filter}
          onChange={toggleFilter}
          loading={isLoadingSites}
          disabled={isLoadingSites}
        />
      </div>

      <FieldArray name={`criteria.groups.${groupIndex}.filters.visit.sites`}>
        {({push, remove}: FieldArrayRenderProps) => (
          <div>
            <ul className="mt-2 pl-14">
              {filter?.sites.map((siteVisitFilter, siteVisitIndex) => (
                <li key={siteVisitIndex}>
                  <div className="flex justify-between sm:items-center">
                    <div className="sm:flex sm:items-center">
                      <div>
                        <Selector
                          isLoading={isLoadingSites}
                          items={siteOptions}
                          selectedId={siteVisitFilter.site_id ?? 0}
                          onChange={item => selectSite(item, siteVisitIndex)}
                        />
                      </div>
                      <div className="w-32 mt-2 sm:mt-0 sm:ml-3">
                        <Field
                          as="select"
                          name={`criteria.groups.${groupIndex}.filters.visit.sites.${siteVisitIndex}.operator`}
                          disabled={!filter}
                        >
                          <option value="<">fewer than</option>
                          <option value="<=">no more than</option>
                          <option value="=">exactly</option>
                          <option value=">=">at least</option>
                          <option value=">">more than</option>
                        </Field>
                      </div>
                      <div className="mt-2 sm:mt-0 sm:ml-3 sm:flex sm:items-center">
                        <div className="w-16">
                          <Field
                            type="number"
                            step={1}
                            min={1}
                            max={65000}
                            name={`criteria.groups.${groupIndex}.filters.visit.sites.${siteVisitIndex}.count`}
                            disabled={!filter}
                          />
                        </div>
                        <span
                          className="ml-1 sm:mt-0 sm:ml-3">{siteVisitFilter.count === 1 ? 'time' : 'times'}</span>
                      </div>
                      <div className="w-32 mt-1 sm:mt-0 sm:ml-3">
                        <Field
                          as="select"
                          name={`criteria.groups.${groupIndex}.filters.visit.sites.${siteVisitIndex}.mode`}
                          disabled={!filter}
                          onChange={(e: FormEvent<HTMLSelectElement>) => selectMode(
                            e.currentTarget.value,
                            siteVisitFilter,
                            siteVisitIndex
                          )}
                        >
                          <option value="ever">ever</option>
                          <option value="between">between</option>
                          <option value="before">before</option>
                          <option value="after">after</option>
                        </Field>
                      </div>
                      <div className="mt-2 sm:mt-0 sm:ml-3">
                        {siteVisitFilter.mode !== 'ever' &&
                          <Field
                            type="date"
                            name={`criteria.groups.${groupIndex}.filters.visit.sites.${siteVisitIndex}.dates.0`}
                          />
                        }
                      </div>
                      <div className="sm:ml-3 sm:flex sm:items-center">
                        {siteVisitFilter.mode === 'between' &&
                          <>
                            <span className="ml-1 sm:ml-0 sm:mr-3">and</span>
                            <Field
                              type="date"
                              name={`criteria.groups.${groupIndex}.filters.visit.sites.${siteVisitIndex}.dates.1`}
                            />
                          </>
                        }
                      </div>
                    </div>

                    {(filter?.sites?.length || 0) > 1 &&
                      <div>
                        <Button
                          color={Button.colors.WHITE}
                          size={Button.sizes.TINY}
                          icon={TrashIcon}
                          onClick={() => remove(siteVisitIndex)}
                        />
                      </div>
                    }
                  </div>

                  {siteVisitIndex !== (filter?.sites.length || 0) - 1 &&
                    <div className="ml-3 my-4 sm:ml-0 sm:my-1 uppercase sm:normal-case">and</div>
                  }
                </li>
              ))}
            </ul>

            {filter &&
              <div className="mt-4 pl-14">
                <Button
                  color={Button.colors.SECONDARY}
                  icon={PlusIcon}
                  size={Button.sizes.TINY}
                  onClick={() => push({...siteVisitFilterTemplate})}
                >
                  Add Site
                </Button>
              </div>
            }
          </div>
        )}
      </FieldArray>
    </div>
  );
}

// Export ----

export default VisitFilter;